<template>
	<div :class="['position-relative', { container: !props.fullWidth }]">
		<div :class="props.tableClasses">
			<header v-if="annualReturns && annualReturns.tableHeading">
				<h2 class="heading-5 mb-100 text-align-center">{{ annualReturns.tableHeading }}</h2>
			</header>
			<BasePlaceHolder :placehold="!annualReturns">
				<table
					v-if="annualReturns"
					class="annual-returns-table table zeta epsilon-lg text-align-center mt-100-smo"
				>
					<thead class="bb-gray-40">
						<tr>
							<th></th>
							<th class="muted body-sm text-align-right">
								<span class="display-block subheading body-md-smo body-weight-bold-smo dark"
									>Fundrise<sup>1</sup></span
								>
								<BaseTooltip
									v-if="props.fundriseColumnDisclaimer"
									position="below"
									class="font-weight-normal"
								>
									{{ props.fundriseColumnDisclaimer }}
								</BaseTooltip>
							</th>
							<th
								v-if="!props.hidePublicReitsColumn"
								class="muted body-sm text-align-right pr-150-md pr-100-smo"
							>
								<span class="display-block subheading body-md-smo body-weight-bold-smo dark"
									>Public REITs<sup>2</sup></span
								>
							</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(assetClass, k) in annualReturns.tableData" :key="k">
							<th class="pl-150-md pl-100-smo">{{ assetClass.year }}</th>
							<td
								:class="{
									'text-color-red': isNegative(assetClass.fundrise)
								}"
								>{{ percentage(assetClass.fundrise, true, 2) }}</td
							>
							<td
								v-if="!props.hidePublicReitsColumn"
								:class="[
									'pr-150-md pr-100-smo',
									{
										'text-color-red': isNegative(assetClass.reits)
									}
								]"
								>{{ percentage(assetClass.reits, true, 2) }}</td
							>
						</tr>
					</tbody>
				</table>
			</BasePlaceHolder>
		</div>
		<slot name="chart_footer"></slot>
	</div>
</template>

<script setup lang="ts">
import { getAnnualReturnsTableData } from '@api/sanity/graphql/charts-data';
import { percentage } from '@filters/shared-filters';
import { ref } from 'vue';
import { SanityAnnualReturnsTable } from 'types/annual-returns';

interface Props {
	tableClasses: string;
	hidePublicReitsColumn?: boolean;
	fundriseColumnDisclaimer?: string;
	fullWidth?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	hidePublicReitsColumn: false,
	fundriseColumnDisclaimer: undefined,
	fullWidth: false
});

const annualReturns = ref<SanityAnnualReturnsTable | null>(null);

function isNegative(value: string | number): boolean {
	return String(value).includes('-');
}

async function setup(): Promise<void> {
	try {
		annualReturns.value = await getAnnualReturnsTableData('annual-returns-table');
	} catch {}
}

setup();
</script>

<style lang="scss" scoped>
@use 'sass:map';
@use '../../styles/constants/colors' as *;
@use '../../styles/utilities/respond-to.scss' as *;

.annual-returns-table {
	@include respond-to(lg) {
		width: 100%;
	}

	&.table > * > * > td,
	&.table > * > * > th {
		border-top: none;
	}

	thead {
		vertical-align: bottom;
		text-align: right;
	}

	tbody {
		tr:first-child {
			th,
			td {
				background-image: none;
			}
		}

		td,
		th {
			background-image: repeating-linear-gradient(
					to right,
					token('content-tertiary') 0%,
					token('content-tertiary') 50%,
					transparent 50%,
					transparent 100%
				),
				repeating-linear-gradient(
					to right,
					token('content-tertiary') 0%,
					token('content-tertiary') 50%,
					transparent 50%,
					transparent 100%
				);
			background-position: top;
			background-repeat: repeat-x;
			background-size:
				0,
				5px 1px;
		}

		td {
			text-align: right;
		}
	}

	tfoot {
		background: map.get($validation-colors, 'info-tint');
	}
}

.bb-gray-40 {
	border-bottom: 1px solid gray(40);
}
</style>
